/**
 * AI CONTEXT
 * Purpose: shared artwork wall (JARVIS-124) — square grid / masonry / strip
 *   with a six-level zoom ladder, version dedupe, and rendition-aware tiles.
 * Contract: `items` + callbacks. NEVER reads Firestore; surfaces own data.
 * Key deps: react-masonry-css, antd Segmented/Slider/ConfigProvider,
 *   useArtworkSrc (T2 renditions), useSentinelPagination.
 * Referenced by: /artwork-wall-debug only (no surface wiring yet).
 * See .cursor/skills/artwork-wall/SKILL.md.
 */
import { ConfigProvider, Empty, Segmented, theme as antdTheme } from "antd"
import { forEach, map } from "lodash"
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import Masonry from "react-masonry-css"
import styled from "styled-components"

import { getRenditionTierForZoom } from "#utils/artworkRenditionHelpers"

import ArtworkTile, { type TileLayout } from "./ArtworkTile"
import {
  ARTWORK_WALL_PREFS_KEY,
  clampGridZoomIndex,
  clampZoomIndex,
  dedupeLatestVersions,
  getColumnsForZoom,
  isStripZoom,
  readWallPrefs,
  stepZoomIndex,
  STRIP_ZOOM_INDEX,
  writeWallPrefs,
  ZOOM_LEVELS,
} from "./artworkWallHelpers"
import {
  WALL_ACCENT,
  WALL_CHROME_BG,
  WALL_STAGE_BG,
  WALL_TEXT,
} from "./artworkWallTheme"
import {
  type ArtworkWallItem,
  type ArtworkWallPrefs,
  type ArtworkWallProps,
  WALL_LAYOUT,
  type WallLayout,
} from "./artworkWallTypes"
import { useSentinelPagination } from "./useSentinelPagination"
import ZoomControl from "./ZoomControl"

const DEFAULT_PAGE_SIZE = 48
const MASONRY_COLUMN_CLASS = "artwork-wall-column"
/**
 * Any of these on the strip means the user has taken over its scroll
 * position, so late centring corrections must stop. Programmatic
 * `scrollIntoView` fires none of them (only `scroll`, which is not listened
 * to — it would be indistinguishable from our own correction).
 */
const USER_SCROLL_EVENTS = [
  "wheel",
  "touchstart",
  "pointerdown",
  "keydown",
] as const

const SRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: ${WALL_STAGE_BG};
  color: ${WALL_TEXT};
  outline: none;
`

const SChrome = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 12px;
  background: ${WALL_CHROME_BG};
  flex: 0 0 auto;
`

const SStage = styled.div<{ $strip: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow: ${({ $strip }) => ($strip ? "hidden" : "auto")};
  background: ${WALL_STAGE_BG};
`

const SGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: 0;
`

/**
 * react-masonry-css over CSS `columns`: it deals items round-robin (item i →
 * column i mod n), which is stable under append, so sentinel pagination never
 * reflows tiles already on screen. CSS `columns` re-balances the whole flow
 * on every append.
 */
const SMasonry = styled(Masonry)`
  display: flex;
  width: 100%;

  & > .${MASONRY_COLUMN_CLASS} {
    display: flex;
    flex-direction: column;
  }
`

const SStrip = styled.div`
  display: flex;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  gap: 0;
`

const SSentinel = styled.div<{ $strip: boolean }>`
  ${({ $strip }) =>
    $strip ? `flex: 0 0 1px; height: 100%;` : `width: 100%; height: 1px;`}
`

const SHint = styled.span`
  font-size: 12px;
  opacity: 0.6;
`

const LAYOUT_OPTIONS = [
  { label: "Square", value: WALL_LAYOUT.SQUARE },
  { label: "Full", value: WALL_LAYOUT.FULL },
]

const readInitialPrefs = (
  key: string,
  initialZoomIndex: number | undefined
): ArtworkWallPrefs => {
  const stored = readWallPrefs(window.localStorage, key)
  return initialZoomIndex === undefined
    ? stored
    : { ...stored, zoomIndex: clampGridZoomIndex(initialZoomIndex) }
}

const ArtworkWall = ({
  items,
  dedupeVersions = true,
  pageSize = DEFAULT_PAGE_SIZE,
  initialZoomIndex,
  prefsStorageKey = ARTWORK_WALL_PREFS_KEY,
  emptyText = "No artwork yet",
  onTileClick,
  onZoomChange,
  onLayoutChange,
  onStripEnter,
  onStripExit,
  className,
  style,
}: ArtworkWallProps) => {
  const [prefs, setPrefs] = useState<ArtworkWallPrefs>(() =>
    readInitialPrefs(prefsStorageKey, initialZoomIndex)
  )
  const [zoomIndex, setZoomIndex] = useState(prefs.zoomIndex)
  const [activeId, setActiveId] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)
  /** True from strip entry until the user scrolls the strip themselves. */
  const entryCenteringRef = useRef(false)

  const inStrip = isStripZoom(zoomIndex)
  const columns = getColumnsForZoom(zoomIndex) ?? 1
  const tier = getRenditionTierForZoom(ZOOM_LEVELS[zoomIndex])
  const tileLayout: TileLayout = inStrip ? "strip" : prefs.layout

  const deduped = useMemo(
    () =>
      dedupeVersions
        ? dedupeLatestVersions(items)
        : { items: [...items], versionCounts: {} },
    [dedupeVersions, items]
  )

  // The stage is the tiles' scroll container, so it is the observer root.
  const { visibleCount, hasMore, sentinelRef } = useSentinelPagination(
    deduped.items.length,
    pageSize,
    stageRef
  )
  const visibleItems = useMemo(
    () => deduped.items.slice(0, visibleCount),
    [deduped.items, visibleCount]
  )

  const persistPrefs = useCallback(
    (next: ArtworkWallPrefs) => {
      setPrefs(next)
      writeWallPrefs(window.localStorage, next, prefsStorageKey)
    },
    [prefsStorageKey]
  )

  const applyZoom = useCallback(
    (nextRaw: number, anchorId: string | null) => {
      const next = clampZoomIndex(nextRaw)
      if (next === zoomIndex) return
      const enteringStrip = isStripZoom(next) && !inStrip
      const leavingStrip = !isStripZoom(next) && inStrip

      if (enteringStrip) {
        const anchor =
          anchorId ??
          visibleItems.find((item) => item.id === activeId)?.id ??
          visibleItems[0]?.id ??
          null
        setActiveId(anchor)
        const anchorItem = visibleItems.find((item) => item.id === anchor)
        if (anchorItem) onStripEnter?.(anchorItem)
      } else {
        persistPrefs({ ...prefs, zoomIndex: next })
        if (leavingStrip) onStripExit?.()
      }

      setZoomIndex(next)
      onZoomChange?.(next)
    },
    [
      activeId,
      inStrip,
      onStripEnter,
      onStripExit,
      onZoomChange,
      persistPrefs,
      prefs,
      visibleItems,
      zoomIndex,
    ]
  )

  const handleZoomControl = useCallback(
    (next: number) => applyZoom(next, null),
    [applyZoom]
  )

  const exitStrip = useCallback(() => {
    // prefs.zoomIndex is the last grid level; the strip never persists.
    applyZoom(prefs.zoomIndex, null)
  }, [applyZoom, prefs.zoomIndex])

  const handleTileClick = useCallback(
    (item: ArtworkWallItem, index: number) => {
      onTileClick?.(item, index)
      if (inStrip) {
        setActiveId(item.id)
        return
      }
      applyZoom(STRIP_ZOOM_INDEX, item.id)
    },
    [applyZoom, inStrip, onTileClick]
  )

  const handleLayoutChange = useCallback(
    (value: string | number) => {
      const layout = value as WallLayout
      persistPrefs({ ...prefs, layout })
      onLayoutChange?.(layout)
    },
    [onLayoutChange, persistPrefs, prefs]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "+" || event.key === "=") {
        event.preventDefault()
        applyZoom(stepZoomIndex(zoomIndex, 1), null)
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault()
        applyZoom(stepZoomIndex(zoomIndex, -1), null)
      } else if (event.key === "Escape" && inStrip) {
        event.preventDefault()
        exitStrip()
      }
    },
    [applyZoom, exitStrip, inStrip, zoomIndex]
  )

  // The tile node is remounted by the layout change, so it is found by id,
  // not by ref. Instant, not smooth: a smooth scroll still in flight when a
  // late correction lands would compound with it.
  const centerActiveTile = useCallback(() => {
    if (!activeId) return
    const tile = stripRef.current?.querySelector<HTMLElement>(
      `[data-artwork-id="${activeId}"]`
    )
    if (tile && typeof tile.scrollIntoView === "function") {
      tile.scrollIntoView({ inline: "center", block: "nearest" })
    }
  }, [activeId])

  // Entering the strip (or picking another tile inside it): centre the
  // active tile and move focus to the wall so Esc / +/- work without another
  // click. Boxes are already right when their aspect is remembered, but a tile
  // decoding with a different aspect shifts everything after it, so the entry
  // transition stays open — re-centring on each geometry change — until the
  // user takes the strip over (wheel, touch, pointer, keys) or leaves it.
  useEffect(() => {
    if (!inStrip || !activeId) return
    const strip = stripRef.current
    entryCenteringRef.current = true
    centerActiveTile()
    rootRef.current?.focus({ preventScroll: true })
    if (!strip) return
    const endEntry = () => {
      entryCenteringRef.current = false
    }
    forEach(USER_SCROLL_EVENTS, (type) =>
      strip.addEventListener(type, endEntry, { passive: true })
    )
    return () => {
      entryCenteringRef.current = false
      forEach(USER_SCROLL_EVENTS, (type) =>
        strip.removeEventListener(type, endEntry)
      )
    }
  }, [activeId, centerActiveTile, inStrip])

  // Fired by tiles whose box changed shape on decode. Only a scroll, never
  // state, so it cannot feed back into itself.
  const handleTileGeometryChange = useCallback(() => {
    if (entryCenteringRef.current) centerActiveTile()
  }, [centerActiveTile])

  const renderTile = (item: ArtworkWallItem, index: number) => (
    <ArtworkTile
      key={item.id}
      item={item}
      index={index}
      layout={tileLayout}
      tier={tier}
      versionCount={deduped.versionCounts[item.id]}
      isActive={inStrip && item.id === activeId}
      onClick={handleTileClick}
      onGeometryChange={handleTileGeometryChange}
    />
  )

  const renderBody = () => {
    if (visibleItems.length === 0) {
      return (
        <Empty
          description={emptyText}
          style={{ padding: 48 }}
          data-testid="artwork-wall-empty"
        />
      )
    }
    if (inStrip) {
      return (
        <SStrip ref={stripRef} data-testid="artwork-wall-strip">
          {map(visibleItems, renderTile)}
          {hasMore ? <SSentinel $strip ref={sentinelRef} /> : null}
        </SStrip>
      )
    }
    if (prefs.layout === WALL_LAYOUT.SQUARE) {
      return (
        <>
          <SGrid $columns={columns} data-testid="artwork-wall-grid">
            {map(visibleItems, renderTile)}
          </SGrid>
          {hasMore ? <SSentinel $strip={false} ref={sentinelRef} /> : null}
        </>
      )
    }
    return (
      <>
        <SMasonry
          breakpointCols={columns}
          className="artwork-wall-masonry"
          columnClassName={MASONRY_COLUMN_CLASS}
          data-testid="artwork-wall-masonry"
        >
          {map(visibleItems, renderTile)}
        </SMasonry>
        {hasMore ? <SSentinel $strip={false} ref={sentinelRef} /> : null}
      </>
    )
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.darkAlgorithm,
        token: {
          borderRadius: 0,
          colorPrimary: WALL_ACCENT,
          colorBgContainer: WALL_CHROME_BG,
        },
      }}
    >
      <SRoot
        ref={rootRef}
        tabIndex={0}
        role="region"
        aria-label="Artwork wall"
        className={className}
        style={style}
        onKeyDown={handleKeyDown}
        data-testid="artwork-wall"
        data-zoom-index={zoomIndex}
        data-layout={tileLayout}
        data-columns={inStrip ? undefined : columns}
      >
        <SChrome>
          <Segmented
            options={LAYOUT_OPTIONS}
            value={prefs.layout}
            onChange={handleLayoutChange}
            disabled={inStrip}
            data-testid="layout-toggle"
          />
          <SHint>
            {inStrip
              ? "Esc to return · + / − to zoom"
              : `${visibleItems.length} of ${deduped.items.length} · + / − to zoom`}
          </SHint>
          <ZoomControl zoomIndex={zoomIndex} onChange={handleZoomControl} />
        </SChrome>
        <SStage
          ref={stageRef}
          $strip={inStrip}
          data-testid="artwork-wall-stage"
        >
          {renderBody()}
        </SStage>
      </SRoot>
    </ConfigProvider>
  )
}

export default ArtworkWall
